import {
	Component,
	inject,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
} from '@angular/core';
import {
	FormControl,
	FormGroup,
	Validators,
	ReactiveFormsModule,
} from '@angular/forms';
import {
	MatDialogRef,
	MatDialog,
	MatDialogContent,
	MatDialogActions,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { BehaviorSubject, Subject, Subscription, takeUntil } from 'rxjs';

import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { UserService } from '@lmc-app/core/services/user.service';
import { ApplicationService } from '@lmc-app/core/services/application.service';
import { CreditorService } from '@lmc-app/core/services/creditor.service';
import { ZohoService } from '@lmc-app/core/services/zoho.service';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { Agency } from '@lmc-app/shared/interface/agency.interface';
import { ModuleFieldPicklist } from '@lmc-app/shared/interface/module-field-picklist';
import { Collector } from '@lmc-app/shared/interface/collector.interface';
import {
	CollectorSelectorModalComponent,
	CollectorSelectorModalData,
} from '@lmc-app/shared/components/collector-selector-modal/collector-selector-modal.component';
import { buildCreateApplicationFormConfig } from './create-application-form.config';

import {
	ValidationErrorDialog,
	ValidationErrorDialogData,
} from './create-application-modal-validation-error-dialog';

import { validateFormControls } from '@lmc-app/shared/formly/validations/application.validation';
import { getFieldLabelWithStep } from './create-application-form-helpers';
import { CreateApplicationFormOptions } from './create-application-form-options.interface';
import { Loading } from '../../applications/loading';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButton } from '@angular/material/button';

@Component({
	selector: 'app-create-application-modal',
	templateUrl: './create-application-modal.component.html',
	styleUrls: ['./create-application-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	providers: [
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: {
				appearance: 'outline', // Sets the border all the way around
				floatLabel: 'always', // Keeps the label in the floating position
			},
		},
	],
	imports: [
		CdkScrollable,
		MatDialogContent,
		ReactiveFormsModule,
		FormlyModule,
		MatDialogActions,
		MatButton,
	],
})
export class CreateApplicationModalComponent implements OnInit, OnDestroy {
	private parentSub = new Subscription();
	private readonly dialogRef = inject(
		MatDialogRef<CreateApplicationModalComponent>
	);
	private _user: any;
	private _validationErrors: string[] = [];

	// Hidden form (Lead Source, Created By)
	private _hiddenForm = new FormGroup({
		leadSource: new FormControl('LMC Platform', Validators.required),
		createdBy: new FormControl(null, Validators.required),
		loanType: new FormControl('Consumer Loan', Validators.required),
	});

	// Single Formly-managed form (stepper pattern)
	protected form: FormGroup = new FormGroup({});

	// Formly field config (stepper with all steps)
	protected fields: FormlyFieldConfig[] = [];

	// Single Formly model (PascalCase keys matching Zoho CRM)
	protected model: any = {};

	// Dropdown data as BehaviorSubjects for Formly observables
	private _agencies$: BehaviorSubject<Agency[]> = new BehaviorSubject<Agency[]>(
		[]
	);
	private _creditors$ = new BehaviorSubject<ModuleFieldPicklist[]>([]);

	private _onDestroy: Subject<void> = new Subject<void>();
	private readonly _snackBar: MatSnackBar = inject(MatSnackBar);
	private readonly _bottomSheet: MatBottomSheet = inject(MatBottomSheet);
	private readonly _dialog: MatDialog = inject(MatDialog);

	constructor(
		private readonly _applicationService: ApplicationService,
		private readonly _userService: UserService,
		private readonly _creditorService: CreditorService,
		private readonly _zohoService: ZohoService,
		private readonly _router: Router
	) {}

	public ngOnInit(): void {
		this.parentSub.add(
			this._userService.user$
				.pipe(takeUntil(this._onDestroy))
				.subscribe(async (user): Promise<void> => {
					if (user) {
						this._user = user;

						this._hiddenForm.patchValue({
							createdBy: this._user.zoho_owner_id,
						});

						await this.initialData();
						try {
							// Build Formly field configs after data loaded
							this.buildFormlyConfigs();
						} catch (error) {
							console.error(error);
						}
					}
				})
		);
	}

	protected onCancel(): void {
		this.dialogRef.close();
	}

	private buildFormlyConfigs(): void {
		const options: CreateApplicationFormOptions = {
			agencies$: this._agencies$.asObservable(),
			creditors$: this._creditors$.asObservable(),
			onAgencyChanged: (): void => this.onAgencyChanged(),
			onCollectorButtonClick: (agency, collector, field): void =>
				this.onCollectorButtonClick(agency, collector, field),
			getAgencyName: (agencyId: string): string => this.getAgencyName(agencyId),
		};

		this.fields = buildCreateApplicationFormConfig(options);
	}

	private initialData(): Promise<boolean> {
		// These methods use BehaviorSubject, so we just call them and resolve immediately
		this.getAgencies();
		this.getCreditors();
		return Promise.resolve(true);
	}

	private markFormGroupTouched(formGroup: FormGroup): void {
		Object.keys(formGroup.controls).forEach((key) => {
			const control = formGroup.get(key);
			if (control instanceof FormGroup) {
				this.markFormGroupTouched(control);
			} else if (control?.value) {
				control.markAsTouched();
				control.updateValueAndValidity({ emitEvent: false });
			}
		});
	}

	private getAgencies(): void {
		this.parentSub.add(
			this._zohoService.getAgencies().subscribe({
				next: (res): void => {
					this._agencies$.next(res);
				},
				error: (error): void => {
					this._snackBar.open(
						error,
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
				},
			})
		);
	}

	private getCreditors() {
		this.parentSub.add(
			this._creditorService.getCreditors().subscribe({
				next: (res) => {
					this._creditors$.next(res);
				},
				error: (error) => {
					console.error(error);
				},
			})
		);
	}

	private validateForm(): boolean {
		this._validationErrors = [];

		// Mark all fields as touched to show validation errors
		this.form.markAllAsTouched();

		// Validate regular form controls using reusable utility
		if (this.form.invalid) {
			validateFormControls(this.form, this._validationErrors, (fields, key) =>
				getFieldLabelWithStep(this.fields, key)
			);

			return false;
		}

		return true;
	}

	protected async submitApplication(): Promise<void> {
		// Validate form before submitting
		if (!this.validateForm()) {
			// Show validation errors in dialog
			const dialogData: ValidationErrorDialogData = {
				errors: this._validationErrors,
			};

			this._dialog.open(ValidationErrorDialog, {
				width: '600px',
				data: dialogData,
			});

			return;
		}

		// Clear validation errors if form is valid
		this._validationErrors = [];

		this._bottomSheet.open(Loading, {
			disableClose: true,
			data: { message: 'Submitting application' },
		});
		const json = this.convertFormToJson();
		this.parentSub.add(
			this._applicationService.createApplication(json).subscribe({
				next: (res) => {
					this._bottomSheet.dismiss();
					const applicationId = res.response.data[0].details.id;
					this._router.navigate([`/applications/summary/${applicationId}`], {
						queryParams: { selectedTab: 'details-general' },
					});
					this.dialogRef.close();
				},
				error: (error) => {
					this._bottomSheet.dismiss();
					console.error(error);
					this._snackBar.open('Error creating application.', 'Close');
				},
			})
		);
	}

	private convertFormToJson(): any {
		const payload: any = { ...this.model };

		// Add metadata
		payload.Stage = 'Incomplete Application';
		payload.Lead_Source = this._hiddenForm.value.leadSource;
		payload.API_Created_By = this._hiddenForm.value.createdBy;
		payload.Owner = this._hiddenForm.value.createdBy;
		payload.Loan_Type = this._hiddenForm.value.loanType;
		payload.Employment_Status1 = payload.Employment_Status;

		// Clean up local-only keys (prefixed with _)
		Object.keys(payload)
			.filter((k) => k.startsWith('_'))
			.forEach((k) => delete payload[k]);

		return payload;
	}

	private onAgencyChanged(): void {
		this.model.Referred_By = null;
		// Collectors will be fetched dynamically by collector-selector type
	}

	private onCollectorButtonClick(
		agency: Agency,
		selectedCollector: Collector | undefined,
		field: FormlyFieldConfig
	): void {
		const dialogData: CollectorSelectorModalData = {
			agency,
			selectedCollector,
		};

		const dialogRef = this._dialog.open(CollectorSelectorModalComponent, {
			width: '80vw',
			height: '80vh',
			maxWidth: '80vw',
			maxHeight: '80vh',
			data: dialogData,
			disableClose: false,
		});

		this.parentSub.add(
			dialogRef.afterClosed().subscribe((collector: Collector | null) => {
				if (collector && field.formControl) {
					field.formControl.setValue(collector);
					field.formControl.markAsTouched();
				}
			})
		);
	}

	private getAgencyName(agencyId: string): string {
		const agency = this._agencies$.value.find((a) => a.id === agencyId);
		return agency?.Account_Name || '';
	}

	public ngOnDestroy(): void {
		this.parentSub.unsubscribe();
		this._onDestroy.next();
		this._onDestroy.complete();
	}
}
